import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_OPTIONS } from "../i18n";
import type { ApiClient, DocumentBlock, ResultDocument, TranslationBlock, TranslationJob } from "../lib/api";
import { localizedError } from "../lib/localized-error";
import { Icon } from "./Icon";

interface Props {
  api: ApiClient;
  documents: ResultDocument[];
  registered: boolean;
  onAuth: () => void;
  onSeek: (milliseconds: number) => void;
}

const TARGET_LANGUAGES = LANGUAGE_OPTIONS.filter(([code]) => code !== "system");

export function TranslationPanel({ api, documents, registered, onAuth, onSeek }: Props) {
  const { t, i18n } = useTranslation();
  const defaultSource = documents.find((document) => document.kind === "subtitle") ?? documents[0];
  const [sourceId, setSourceId] = useState(defaultSource?.id ?? "");
  const [sourceBlocks, setSourceBlocks] = useState<DocumentBlock[]>(defaultSource?.blocks ?? []);
  const [jobs, setJobs] = useState<TranslationJob[]>([]);
  const [activeJob, setActiveJob] = useState<TranslationJob | null>(null);
  const [targetLanguage, setTargetLanguage] = useState(() => defaultTarget(defaultSource?.language ?? null));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const source = documents.find((document) => document.id === sourceId) ?? defaultSource;
  const targetBlocks = activeJob?.blocks ?? [];
  const targetBySource = useMemo(() => groupTargetBlocks(targetBlocks), [targetBlocks]);
  const qualityCount = useMemo(
    () => targetBlocks.reduce((total, block) => total + qualityIssues(block).length, 0),
    [targetBlocks],
  );
  const languageNames = useMemo(
    () => new Intl.DisplayNames([i18n.resolvedLanguage ?? i18n.language], { type: "language" }),
    [i18n.language, i18n.resolvedLanguage],
  );
  const localizedLanguage = (code: string) => languageNames.of(code) ?? code;

  useEffect(() => {
    if (!sourceId && defaultSource) setSourceId(defaultSource.id);
  }, [defaultSource, sourceId]);

  useEffect(() => {
    if (!sourceId) return;
    let active = true;
    setLoading(true);
    setError(null);
    void Promise.all([api.getDocument(sourceId), api.listTranslations(sourceId)]).then(([document, items]) => {
      if (!active) return;
      setSourceBlocks(document.blocks);
      setJobs(items);
      const selected = items.find((job) => job.target_language === targetLanguage) ?? items[0] ?? null;
      setActiveJob(selected);
      if (selected) setTargetLanguage(selected.target_language);
    }).catch((reason) => {
      if (active) setError(localizedError(reason, t));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [api, sourceId, t]);

  useEffect(() => {
    const job = activeJob;
    if (!job || (job.status !== "queued" && job.status !== "running")) return;
    let active = true;
    const poll = window.setInterval(() => {
      void api.getTranslation(job.id).then((latest) => {
        if (!active) return;
        setActiveJob(latest);
        setJobs((current) => replaceJob(current, latest));
      }).catch((reason) => {
        if (active) setError(localizedError(reason, t));
      });
    }, 2_000);
    return () => { active = false; window.clearInterval(poll); };
  }, [activeJob, api, t]);

  useEffect(() => {
    const selected = jobs.find((job) => job.target_language === targetLanguage) ?? null;
    setActiveJob(selected);
    if (!selected || (selected.status !== "completed" && selected.status !== "stale") || selected.blocks) return;
    let active = true;
    void api.getTranslation(selected.id).then((job) => {
      if (!active) return;
      setActiveJob(job);
      setJobs((current) => replaceJob(current, job));
    }).catch((reason) => {
      if (active) setError(localizedError(reason, t));
    });
    return () => { active = false; };
  }, [api, jobs, t, targetLanguage]);

  async function createTranslation() {
    if (!registered) { onAuth(); return; }
    if (!sourceId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createTranslation(sourceId, targetLanguage);
      setJobs((current) => replaceJob(current, created));
      setActiveJob(created);
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(false);
    }
  }

  async function retry() {
    if (!activeJob) return;
    setBusy(true);
    setError(null);
    try {
      const retried = await api.retryTranslation(activeJob.id);
      setJobs((current) => replaceJob(current, retried));
      setActiveJob(retried);
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!activeJob) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteTranslation(activeJob.id);
      const remaining = jobs.filter((job) => job.id !== activeJob.id);
      setJobs(remaining);
      setActiveJob(remaining.find((job) => job.target_language === targetLanguage) ?? null);
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setBusy(false);
    }
  }

  const comparisonSource = activeJob?.source_blocks ?? sourceBlocks;
  const ready = activeJob?.status === "completed" || activeJob?.status === "stale";

  return <main className="translation-panel">
    <header className="translation-panel__header">
      <div><p className="eyebrow">{t("translation.eyebrow")}</p><h2>{t("translation.title")}</h2><p>{t("translation.description")}</p></div>
      <div className="translation-controls">
        <label><span>{t("translation.source")}</span><select value={sourceId} onChange={(event) => {
          const next = documents.find((document) => document.id === event.target.value);
          setSourceId(event.target.value);
          setTargetLanguage(defaultTarget(next?.language ?? null));
          setActiveJob(null);
        }}>{documents.map((document) => <option key={document.id} value={document.id}>{t(`editor.${document.kind === "subtitle" ? "subtitles" : "transcript"}`)}</option>)}</select></label>
        <label><span>{t("translation.target")}</span><select value={targetLanguage} onChange={(event) => setTargetLanguage(event.target.value)}>{TARGET_LANGUAGES.filter(([code]) => languageBase(code) !== languageBase(source?.language)).map(([code]) => <option key={code} value={code}>{localizedLanguage(code)}</option>)}</select></label>
        <button type="button" className="button button--primary" disabled={busy || loading || !sourceId} onClick={() => void createTranslation()}><Icon name="wave" />{busy ? t("common.loading") : activeJob?.status === "stale" ? t("translation.retranslate") : t("translation.translate")}</button>
      </div>
    </header>

    {!registered && <div className="translation-banner"><Icon name="check" /><p><strong>{t("translation.accountRequired")}</strong><span>{t("translation.accountHint")}</span></p><button type="button" onClick={onAuth}>{t("dashboard.signIn")}</button></div>}
    {error && <div className="translation-error" role="alert"><Icon name="warning" /><span>{error}</span></div>}
    {activeJob && <div className={`translation-status translation-status--${activeJob.status}`}><div><i /><strong>{t(`translation.status.${activeJob.status}`)}</strong><span>{t("translation.jobLanguage", { language: localizedLanguage(activeJob.target_language) })}</span>{qualityCount > 0 && ready && <em>{t("translation.qualityCount", { count: qualityCount })}</em>}</div><div>{activeJob.status === "failed" && <button type="button" disabled={busy} onClick={() => void retry()}>{t("common.retry")}</button>}{activeJob.status === "stale" && <span>{t("translation.staleHint")}</span>}<button type="button" disabled={busy} aria-label={t("translation.delete")} onClick={() => void remove()}><Icon name="trash" /></button></div></div>}

    {loading ? <div className="translation-empty"><i className="translation-spinner" /><strong>{t("common.loading")}</strong></div>
      : !activeJob ? <div className="translation-empty"><Icon name="wave" /><strong>{t("translation.empty")}</strong><span>{t("translation.emptyHint")}</span></div>
      : !ready ? <div className="translation-empty"><i className="translation-spinner" /><strong>{t(`translation.status.${activeJob.status}`)}</strong><span>{activeJob.status === "failed" ? t("translation.failedHint") : t("translation.processingHint")}</span></div>
      : <section className="translation-comparison" aria-label={t("translation.comparison")}> <div className="translation-comparison__head"><strong>{t("translation.sourceText")}</strong><strong>{t("translation.translatedText")}</strong></div>{comparisonSource.map((block, index) => {
        const translated = targetBySource.get(block.id ?? "") ?? [];
        const blockIssues = translated.flatMap(qualityIssues);
        return <article key={block.id ?? index} onClick={() => onSeek(block.start_ms)}><div><time>{formatTimestamp(block.start_ms)}</time><p>{block.text}</p></div><div><time>{localizedLanguage(activeJob.target_language)}</time>{translated.length ? translated.map((entry) => <p key={entry.id}>{entry.text}</p>) : <p className="translation-missing">{t("translation.missing")}</p>}{blockIssues.length > 0 && <ul>{[...new Set(blockIssues)].map((issue) => <li key={issue}><Icon name="warning" />{t(`translation.quality.${issue}`)}</li>)}</ul>}</div></article>;
      })}</section>}
  </main>;
}

function replaceJob(jobs: TranslationJob[], job: TranslationJob): TranslationJob[] {
  return [job, ...jobs.filter((item) => item.id !== job.id)].sort((left, right) => right.created_at_ms - left.created_at_ms);
}

function groupTargetBlocks(blocks: TranslationBlock[]): Map<string, TranslationBlock[]> {
  const result = new Map<string, TranslationBlock[]>();
  for (const block of blocks) result.set(block.source_block_id, [...(result.get(block.source_block_id) ?? []), block]);
  return result;
}

function qualityIssues(block: TranslationBlock): string[] {
  return Array.isArray(block.metadata.quality_issues) ? block.metadata.quality_issues.filter((value): value is string => typeof value === "string") : [];
}

function defaultTarget(sourceLanguage: string | null): string {
  return languageBase(sourceLanguage) === "en" ? "zh-CN" : "en";
}

function languageBase(value: string | null | undefined): string {
  return value?.replaceAll("_", "-").split("-")[0]?.toLowerCase() ?? "";
}

function formatTimestamp(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1_000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}
